import type { IntlShape } from 'react-intl';

import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import type { ChartUnit } from '../../../core/types/chart-unit.js';
import type { Formatter } from '../../../core/types/formatter.js';
import type { SeriesActionsTemplate } from '../../../core/types/series-actions-template.js';
import { makeAccessor } from '../../../core/utils/makeAccessor.js';
import type { XYChartTooltipPayload } from '../../../xy-chart/types/xy-chart.js';
import { FLAME_TOOLTIP_MESSAGES } from '../components/tooltip/messages.js';
import {
  CHILDREN_COUNT_FIELD,
  FLAME_ORIGINAL_IDX,
  SELF_VALUE_FIELD,
} from '../constants.js';
import { formatFlameValue } from './format-flame-value.js';

interface BuildFlameTooltipBaseDataArgs<TData extends ChartDatapoint> {
  payload: XYChartTooltipPayload;
  data: readonly TData[];
  labelAccessor: string;
  selfValueAccessor?: string;
  formatter?: Formatter;
  unit?: ChartUnit;
  seriesActions?: (datapoint: TData) => SeriesActionsTemplate;
  intl: IntlShape;
}

export function buildFlameTooltipBaseData<TData extends ChartDatapoint>({
  payload,
  data,
  labelAccessor,
  selfValueAccessor,
  formatter,
  unit,
  seriesActions,
  intl,
}: Readonly<BuildFlameTooltipBaseDataArgs<TData>>) {
  const datapoint = payload.rectPayload?.datapoint;
  const color = `${payload.rectPayload?.color}`;

  const readLabel = makeAccessor(labelAccessor);
  const rawLabel = datapoint !== undefined ? readLabel(datapoint) : undefined;
  const label =
    typeof rawLabel === 'string' ||
    typeof rawLabel === 'number' ||
    typeof rawLabel === 'boolean'
      ? String(rawLabel)
      : '';

  const childrenCount = Number(datapoint?.[CHILDREN_COUNT_FIELD] ?? 0);
  const subheader =
    childrenCount > 0
      ? intl.formatMessage(FLAME_TOOLTIP_MESSAGES.children, {
          count: childrenCount,
        })
      : undefined;

  const originalDatapoint =
    datapoint !== undefined
      ? data[datapoint[FLAME_ORIGINAL_IDX] as number]
      : undefined;

  const actions =
    seriesActions && originalDatapoint
      ? () => seriesActions(originalDatapoint)
      : undefined;

  const selfValue =
    selfValueAccessor !== undefined
      ? formatFlameValue(datapoint?.[SELF_VALUE_FIELD], formatter, unit)
      : undefined;

  return {
    datapoint,
    color,
    label,
    subheader,
    originalDatapoint,
    actions,
    selfValue,
  };
}
